import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ChoiceGroup, Text } from '@fluentui/react';

export const PrimaryPurpose = {
  SelfAdvertising: 1,
  Managing: 2,
};

const getPrimaryUseStyles = () => ({
  primaryPurpose: {
    marginTop: '16px',
    marginBottom: '16px',
    '& .title span': {
      fontWeight: 600,
    },
    '& .sub-title': {
      marginTop: '5px',
      marginBottom: '12px',
    },
    '& label': {
      fontWeight: 400,
      padding: 'unset',
    },
    '& .primary-purpose-error': {
      color: '#a4262c',
      marginTop: '5px',
    },
  },
});

export const isPrimaryUseSelected = selectedPrimaryUse => (
  selectedPrimaryUse === PrimaryPurpose.SelfAdvertising
  || selectedPrimaryUse === PrimaryPurpose.Managing
);

export function BusinessDetailsPrimaryUse(props) {
  const {
    selectedPrimaryUse,
    setSelectedPrimaryUse,
    showAgencyIdentification,
    showValidationResult,
  } = props;

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getPrimaryUseStyles);
  const primaryUseId = 'primary-use-label';
  const errorId = 'primary-use-error';

  const primaryPurposeOptions = useMemo(() => ([
    {
      key: PrimaryPurpose.SelfAdvertising,
      text: showAgencyIdentification
        ? i18n.getString(_TL_('Promote my business'))
        : i18n.getString(_TL_('To promote this business')),
      labelId: 'self-advertising-label',
    },
    {
      key: PrimaryPurpose.Managing,
      text: showAgencyIdentification
        ? i18n.getString(_TL_('Manage advertising for clients as an agency'))
        : i18n.getString(_TL_('To provide services to other businesses as an ad agency')),
      labelId: 'managing-label',
    },
  ]), [i18n, showAgencyIdentification]);

  const onSelectionChange = useCallback((data, option) => {
    setSelectedPrimaryUse(option.key);
  }, [setSelectedPrimaryUse]);

  return (
    <div className={classNames(classes.primaryPurpose, 'primary-purpose')}>
      <div className="title">
        <Text id={primaryUseId}>
          {i18n.getString(_TL_('Primary use'))}
        </Text>
      </div>
      <div className="sub-title">
        <Text>
          {showAgencyIdentification
            ? i18n.getString(_TL_('How will you use this account?'))
            : i18n.getString(_TL_("Let us know how you'll use this account."))
          }
        </Text>
      </div>
      <div className="primary-purpose-choice-group">
        <ChoiceGroup
          options={primaryPurposeOptions}
          onChange={onSelectionChange}
          selectedKey={selectedPrimaryUse}
          required={showAgencyIdentification}
          ariaLabelledBy={primaryUseId}
          ariaDescribedBy={showValidationResult ? errorId : undefined}
        />
      </div>
      {showValidationResult &&
        <Text id={errorId} className="primary-purpose-error" role="alert">
          {i18n.getString(_TL_('Select an option.'))}
        </Text>
      }
    </div>
  );
}

BusinessDetailsPrimaryUse.propTypes = {
  selectedPrimaryUse: PropTypes.oneOf(['', PrimaryPurpose.SelfAdvertising, PrimaryPurpose.Managing]).isRequired,
  setSelectedPrimaryUse: PropTypes.func.isRequired,
  showAgencyIdentification: PropTypes.bool,
  showValidationResult: PropTypes.bool,
};

BusinessDetailsPrimaryUse.defaultProps = {
  showAgencyIdentification: false,
  showValidationResult: false,
};
